import React, { PropsWithChildren, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Transition } from 'react-transition-group';
import classNames from 'classnames';

import { MenuLayerLevel } from '../types';

import { ModulesScrollbar } from 'app/design-system/components/Scrollbar';

import { preventClick } from '../dom-utils';
import { useLeftMenuStateAPI } from '../hooks/left-menu-state-api';
import { leftMenuTransitionDuration } from '../styles/animation-variables';
import { layerLevelStyles, layerTransitionStyles } from '../styles/layer-state-styles';

import styles from '../styles/LeftMenu.module.scss';

type LayerProps = PropsWithChildren<{
  level: MenuLayerLevel;
  header?: {
    title: string;
    location?: string;
  };
  visible: boolean;
}>;

const Layer: React.FC<LayerProps> = ({
  level,
  header,
  visible,
  children,
}) => {
  const { createLayerMouseEnterHandler, expanded } = useLeftMenuStateAPI();

  const handleMouseEnter = useCallback(
    createLayerMouseEnterHandler(level),
    [createLayerMouseEnterHandler],
  );

  const headerNode = (
    header?.location
      ? (
        <Link to={header.location}
              className={styles.header}
              onClick={expanded ? preventClick : undefined}
              tabIndex={expanded ? -1 : 0}
              data-test="layer-menu-header">
          <span>{header.title}</span>
        </Link>
      )
      : (
        <div className={styles.header}
             data-test="layer-menu-header">
          {header?.title || ' '}
        </div>
      )
  );

  const bodyNode = (
    <div className={styles.body}>
      <ModulesScrollbar>
        {children}
      </ModulesScrollbar>
    </div>
  );

  return (
    <Transition in={visible}
                timeout={leftMenuTransitionDuration}
                mountOnEnter
                unmountOnExit>
      {(state): React.FunctionComponentElement<HTMLDivElement> => {
        const className = classNames(
          styles.layer,
          layerLevelStyles[level],
          layerTransitionStyles[state],
        );

        return (
          <div className={className}
               data-test-layer-level={level}
               onMouseEnter={handleMouseEnter}>
            {headerNode}
            {bodyNode}
          </div>
        );
      }}
    </Transition>
  );
};

export default Layer;
